import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setApiJson } from '../../../redux/Actions/ApiAction'
import { HitApi } from '../../../Api/ApiHit'
import FileuploadComp from '../../../components/SingleFileUpload/FileuploadComp'
import {
    addAppSectionClient,
    updateAppSectionClient,
    addServiceToSection,
    removeServiceFromSection,
    addCategoryToSection,
    removeCategoryFromSection,
    searchCategory,
    searchSubCategory,
    searchService
} from '../../../constant/Constant'
import { getSelectedUnit } from '../../../storage/Storage'
import toast from 'react-hot-toast'
import {
    X, Loader2, Plus, Trash2,
    ArrowLeftRight, LayoutGrid, List,
    Layers, Grid3X3
} from 'lucide-react'

const SectionModal = ({ section, parentServices, unitId, projectId, serviceType = 'salon', onClose }) => {
    const isEditing = !!section
    const dispatch = useDispatch()
    const ApiReducer = useSelector(state => state.ApiReducer)

    // Form state
    const [title, setTitle] = useState(section?.title || '')
    const [description, setDescription] = useState(section?.description || '')
    const [gender, setGender] = useState(section?.gender || 'all')
    const [displayType, setDisplayType] = useState(section?.displayType || 'horizontal_scroll')
    const [sectionServiceType, setSectionServiceType] = useState(section?.serviceType || serviceType || '')
    const [sectionServices, setSectionServices] = useState(
        (section?.services || []).filter(s => s.serviceId !== null && s.serviceId !== undefined)
    )

    // Section type state
    const [sectionType, setSectionType] = useState(section?.sectionType || 'services')
    const [sectionCategories, setSectionCategories] = useState(
        (section?.categories || []).filter(c => c.categoryId !== null || c.subCategoryId !== null)
    )

    // Whether the categories section adds 'category' items or 'subcategory' items (not both)
    const detectInitialCategoryItemType = () => {
        const cats = (section?.categories || []).filter(c => c.categoryId !== null || c.subCategoryId !== null)
        if (cats.length === 0) return 'category'
        return cats[0]?.itemType === 'subcategory' ? 'subcategory' : 'category'
    }
    const [categoryItemType, setCategoryItemType] = useState(detectInitialCategoryItemType)

    // Cascading dropdown state
    const [selectedGroup, setSelectedGroup] = useState(null)
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [selectedSubCategory, setSelectedSubCategory] = useState(null)

    const [categories, setCategories] = useState([])
    const [subCategories, setSubCategories] = useState([])
    const [services, setServices] = useState([])

    const [loadingCategories, setLoadingCategories] = useState(false)
    const [loadingSubCategories, setLoadingSubCategories] = useState(false)
    const [loadingServices, setLoadingServices] = useState(false)

    // Validation errors
    const [errors, setErrors] = useState({})

    // Loading states
    const [saving, setSaving] = useState(false)
    const [addingService, setAddingService] = useState(null)
    const [addingCategory, setAddingCategory] = useState(null)

    // ── Cascading handlers ──────────────────────────────────────────

    const handleGroupChange = async (groupId) => {
        const group = parentServices.find(g => g._id === groupId) || null
        setSelectedGroup(group)
        setSelectedCategory(null)
        setSelectedSubCategory(null)
        setCategories([])
        setSubCategories([])
        setServices([])

        if (!groupId) return
        setLoadingCategories(true)
        try {
            const res = await HitApi({
                page: 1,
                limit: 100000,
                groupId,
                unitIds: unitId || getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }, searchCategory)
            setCategories(res?.data?.docs || [])
        } catch {
            toast.error('Failed to fetch categories')
        }
        setLoadingCategories(false)
    }

    const handleCategoryChange = async (categoryId) => {
        const category = categories.find(c => c._id === categoryId) || null
        setSelectedCategory(category)
        setSelectedSubCategory(null)
        setSubCategories([])
        setServices([])

        if (!categoryId) return
        setLoadingSubCategories(true)
        try {
            const res = await HitApi({
                page: 1,
                limit: 100000,
                categoryId
            }, searchSubCategory)
            setSubCategories(res?.data?.docs || [])
        } catch {
            toast.error('Failed to fetch subcategories')
        }
        setLoadingSubCategories(false)
    }

    const handleSubCategoryChange = async (subCategoryId) => {
        const subCategory = subCategories.find(s => s._id === subCategoryId) || null
        setSelectedSubCategory(subCategory)
        setServices([])

        if (!subCategoryId) return
        setLoadingServices(true)
        try {
            const res = await HitApi({
                page: 1,
                limit: 100000,
                search: { subCategoryId, groupId: selectedGroup?._id }
            }, searchService)
            setServices(res?.data?.docs || [])
        } catch {
            toast.error('Failed to fetch services')
        }
        setLoadingServices(false)
    }

    // ── Pre-populate image in Redux for edit mode / clear for create mode ──
    useEffect(() => {
        dispatch(setApiJson({
            ...(ApiReducer.apiJson || {}),
            sectionImage: section?.image || ''
        }))
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Auto-detect group from existing services in edit mode ───────
    useEffect(() => {
        if (!isEditing || sectionServices.length === 0 || parentServices.length === 0) return
        const firstService = sectionServices[0]
        const serviceObj = firstService?.serviceId
        if (!serviceObj || typeof serviceObj !== 'object') return
        const groupId = serviceObj.groupId?._id || serviceObj.groupId
        if (!groupId) return
        const matchedGroup = parentServices.find(g => g._id === groupId)
        if (matchedGroup) {
            handleGroupChange(matchedGroup._id)
        }
    }, [isEditing, parentServices]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Service add / remove ────────────────────────────────────────

    const isServiceInSection = (serviceId) =>
        sectionServices.some(s => (s.serviceId?._id || s.serviceId) === serviceId)

    const checkAndUpdateGender = (newService) => {
        if (gender === 'all') return

        const newServiceGender = newService.gender?.toLowerCase()
        if (!newServiceGender || newServiceGender === 'all') return

        // Check if new service conflicts with current section gender setting
        const mismatchWithSection = newServiceGender !== gender

        // Check if new service conflicts with already-added services
        const existingGenders = sectionServices
            .filter(s => s.serviceId && typeof s.serviceId === 'object')
            .map(s => s.serviceId.gender?.toLowerCase())
            .filter(g => g && g !== 'all')

        const mismatchWithExisting = existingGenders.some(g => g !== newServiceGender)

        if (mismatchWithSection || mismatchWithExisting) {
            setGender('all')
            toast('Section gender set to "All" because you have both Male and Female services.', { icon: 'ℹ️' })
        }
    }

    const handleAddService = async (service) => {
        checkAndUpdateGender(service)
        if (!isEditing) {
            setSectionServices(prev => [...prev, { serviceId: service, order: prev.length }])
            return
        }
        setAddingService(service._id)
        try {
            const res = await HitApi({
                sectionId: section._id,
                serviceId: service._id,
                order: sectionServices.length
            }, addServiceToSection)
            if (res?.success || res?.statusCode === 200 || res?.statusCode === 201) {
                setSectionServices(res.data?.services || res.data?.docs || sectionServices)
                toast.success('Service added')
            } else {
                toast.error(res?.message || 'Failed to add service')
            }
        } catch {
            toast.error('Failed to add service')
        }
        setAddingService(null)
    }

    const handleRemoveService = async (serviceId) => {
        const actualId = serviceId?._id || serviceId
        if (!isEditing) {
            setSectionServices(prev => prev.filter(s => (s.serviceId?._id || s.serviceId) !== actualId))
            return
        }
        try {
            const res = await HitApi({ sectionId: section._id, serviceId: actualId }, removeServiceFromSection)
            if (res?.success || res?.statusCode === 200) {
                setSectionServices(res.data?.services || res.data?.docs || [])
                toast.success('Service removed')
            } else {
                toast.error(res?.message || 'Failed to remove service')
            }
        } catch {
            toast.error('Failed to remove service')
        }
    }

    // ── Category Item Type Toggle ───────────────────────────────────
    const handleCategoryItemTypeChange = (newType) => {
        if (newType === categoryItemType) return
        if (sectionCategories.length > 0) {
            if (!window.confirm(`Switching will clear the ${sectionCategories.length} item(s) already added. Continue?`)) return
            setSectionCategories([])
        }
        setSelectedCategory(null)
        setSelectedSubCategory(null)
        setSubCategories([])
        setCategoryItemType(newType)
    }

    // ── Category add / remove ────────────────────────────────────────

    const isCategoryInSection = (categoryId, itemType) => {
        return sectionCategories.some(c => {
            if (itemType === 'category') {
                return (c.categoryId?._id || c.categoryId) === categoryId && c.itemType === 'category'
            }
            return (c.subCategoryId?._id || c.subCategoryId) === categoryId && c.itemType === 'subcategory'
        })
    }

    const handleAddCategory = async (category, itemType = 'category') => {
        const categoryData = {
            itemType,
            order: sectionCategories.length
        }
        if (itemType === 'category') {
            categoryData.categoryId = category
        } else {
            categoryData.subCategoryId = category
            if (selectedCategory) categoryData.categoryId = selectedCategory
        }

        if (!isEditing) {
            setSectionCategories(prev => [...prev, categoryData])
            return
        }

        const itemId = category._id || category
        setAddingCategory(itemId)
        try {
            const payload = {
                sectionId: section._id,
                itemType,
                order: sectionCategories.length
            }
            if (itemType === 'category') {
                payload.categoryId = itemId
            } else {
                payload.subCategoryId = itemId
                if (selectedCategory?._id) payload.categoryId = selectedCategory._id
            }

            const res = await HitApi(payload, addCategoryToSection)
            if (res?.success || res?.statusCode === 200 || res?.statusCode === 201) {
                setSectionCategories(res.data?.categories || res.data?.docs || sectionCategories)
                toast.success('Category added')
            } else {
                toast.error(res?.message || 'Failed to add category')
            }
        } catch {
            toast.error('Failed to add category')
        }
        setAddingCategory(null)
    }

    const handleRemoveCategory = async (categoryId, itemType) => {
        const actualId = categoryId?._id || categoryId
        if (!isEditing) {
            setSectionCategories(prev => prev.filter(c => {
                if (itemType === 'category') {
                    return !((c.categoryId?._id || c.categoryId) === actualId && c.itemType === 'category')
                }
                return !((c.subCategoryId?._id || c.subCategoryId) === actualId && c.itemType === 'subcategory')
            }))
            return
        }
        try {
            const payload = {
                sectionId: section._id,
                itemType
            }
            if (itemType === 'category') {
                payload.categoryId = actualId
            } else {
                payload.subCategoryId = actualId
            }

            const res = await HitApi(payload, removeCategoryFromSection)
            if (res?.success || res?.statusCode === 200) {
                setSectionCategories(res.data?.categories || res.data?.docs || [])
                toast.success('Category removed')
            } else {
                toast.error(res?.message || 'Failed to remove category')
            }
        } catch {
            toast.error('Failed to remove category')
        }
    }

    // ── Section Type Change ────────────────────────────────────────

    const handleSectionTypeChange = (newType) => {
        if (newType === sectionType) return

        // Warn if switching types will clear items
        const hasServices = sectionServices.length > 0
        const hasCategories = sectionCategories.length > 0

        if ((newType === 'categories' && hasServices) || (newType === 'services' && hasCategories)) {
            const itemsToLose = newType === 'categories' ? 'services' : 'categories'
            if (!window.confirm(`Switching type will clear your current ${itemsToLose}. Continue?`)) {
                return
            }
        }

        setSectionType(newType)
        // Clear items when switching types in create mode
        if (!isEditing) {
            if (newType === 'categories') {
                setSectionServices([])
            } else {
                setSectionCategories([])
            }
        }
    }

    // ── Submit ──────────────────────────────────────────────────────

    const validate = () => {
        const newErrors = {}
        if (!title.trim()) newErrors.title = 'Title is required'
        if (!sectionServiceType.trim()) newErrors.sectionServiceType = 'Service Type is required'
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validate()) return
        setSaving(true)
        try {
            const payload = {
                unitIds: unitId,
                projectId,
                title: title.trim(),
                description: description.trim(),
                gender,
                displayType,
                serviceType: sectionServiceType.trim(),
                sectionType,
                ...(ApiReducer.apiJson?.sectionImage ? { image: ApiReducer.apiJson.sectionImage } : {})
            }
            if (isEditing) {
                payload._id = section._id
                const res = await HitApi(payload, updateAppSectionClient)
                if (res?.success || res?.statusCode === 200) { toast.success('Section updated'); onClose(true) }
                else toast.error(res?.message || 'Failed to update section')
            } else {
                const res = await HitApi(payload, addAppSectionClient)
                if (res?.success || res?.statusCode === 201) {
                    const newSectionId = res?.data?._id || res?.data?.doc?._id || res?.data?.docs?._id
                    if (newSectionId) {
                        if (sectionType === 'services' && sectionServices.length > 0) {
                            for (let i = 0; i < sectionServices.length; i++) {
                                const s = sectionServices[i]
                                await HitApi({
                                    sectionId: newSectionId,
                                    serviceId: s.serviceId?._id || s.serviceId,
                                    order: i
                                }, addServiceToSection).catch(() => {})
                            }
                        } else if (sectionType === 'categories' && sectionCategories.length > 0) {
                            for (let i = 0; i < sectionCategories.length; i++) {
                                const c = sectionCategories[i]
                                const catPayload = { sectionId: newSectionId, itemType: c.itemType, order: i }
                                if (c.itemType === 'category') {
                                    catPayload.categoryId = c.categoryId?._id || c.categoryId
                                } else {
                                    catPayload.subCategoryId = c.subCategoryId?._id || c.subCategoryId
                                    if (c.categoryId) catPayload.categoryId = c.categoryId?._id || c.categoryId
                                }
                                await HitApi(catPayload, addCategoryToSection).catch(() => {})
                            }
                        }
                    }
                    toast.success('Section created')
                    onClose(true)
                } else {
                    toast.error(res?.message || 'Failed to create section')
                }
            }
        } catch {
            toast.error('Failed to save section')
        }
        setSaving(false)
    }

    const displayTypes = [
        { value: 'horizontal_scroll', label: 'Horizontal Scroll', icon: ArrowLeftRight },
        { value: 'grid', label: 'Grid', icon: LayoutGrid },
        { value: 'list', label: 'List', icon: List }
    ]

    const selectClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">

                {/* Header */}
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {isEditing ? 'Edit Section' : 'Create New Section'}
                    </h2>
                    <button onClick={() => onClose(false)}>
                        <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="grid grid-cols-2 gap-6">

                            {/* ── Left Column: Section Details ── */}
                            <div className="space-y-5">
                                <h3 className="font-medium text-gray-900 border-b pb-2">Section Details</h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => { setTitle(e.target.value); setErrors(prev => ({ ...prev, title: '' })) }}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${errors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                        placeholder="e.g., Best Services, Men's Special"
                                    />
                                    {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                    <input
                                        type="text"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                        placeholder="Optional subtitle"
                                    />
                                </div>

                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-2">
                                        Section Image <span className="text-gray-400 font-normal">(optional — used as service type tab icon)</span>
                                    </p>
                                    <FileuploadComp
                                        name="sectionImage"
                                        allowed={['img']}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Service Type *</label>
                                    <input
                                        type="text"
                                        value={sectionServiceType}
                                        onChange={(e) => { setSectionServiceType(e.target.value); setErrors(prev => ({ ...prev, sectionServiceType: '' })) }}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${errors.sectionServiceType ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                        placeholder="e.g., salon, athome, kiosk"
                                    />
                                    {errors.sectionServiceType
                                        ? <p className="text-xs text-red-500 mt-1">{errors.sectionServiceType}</p>
                                        : <p className="text-xs text-gray-400 mt-1">Type of service this section belongs to</p>
                                    }
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Show for Gender</label>
                                    <div className="flex gap-2">
                                        {['all', 'male', 'female'].map((g) => {
                                            const serviceGenders = sectionServices
                                                .filter(s => s.serviceId && typeof s.serviceId === 'object')
                                                .map(s => s.serviceId.gender?.toLowerCase())
                                                .filter(sg => sg && sg !== 'all')
                                            const hasMixed = serviceGenders.length > 0 &&
                                                serviceGenders.some(sg => sg !== serviceGenders[0])
                                            const isDisabled = hasMixed && g !== 'all'
                                            return (
                                                <button
                                                    key={g}
                                                    type="button"
                                                    onClick={() => {
                                                        if (isDisabled) {
                                                            toast('Cannot switch to a single gender — section has both Male and Female services.', { icon: '⚠️' })
                                                            return
                                                        }
                                                        setGender(g)
                                                    }}
                                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                        gender === g
                                                            ? g === 'male' ? 'bg-blue-600 text-white'
                                                                : g === 'female' ? 'bg-pink-600 text-white'
                                                                    : 'bg-gray-800 text-white'
                                                            : isDisabled
                                                                ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {g.charAt(0).toUpperCase() + g.slice(1)}
                                                </button>
                                            )
                                        })}
                                    </div>
                                    {(() => {
                                        const serviceGenders = sectionServices
                                            .filter(s => s.serviceId && typeof s.serviceId === 'object')
                                            .map(s => s.serviceId.gender?.toLowerCase())
                                            .filter(sg => sg && sg !== 'all')
                                        const hasMixed = serviceGenders.length > 0 &&
                                            serviceGenders.some(sg => sg !== serviceGenders[0])
                                        return hasMixed ? (
                                            <p className="text-xs text-amber-600 mt-1.5">
                                                Mixed genders detected — remove services to unlock Male/Female options.
                                            </p>
                                        ) : null
                                    })()}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Type</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {displayTypes.map((dt) => (
                                            <button
                                                key={dt.value}
                                                type="button"
                                                onClick={() => setDisplayType(dt.value)}
                                                className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                                                    displayType === dt.value
                                                        ? 'border-blue-500 bg-blue-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                            >
                                                <dt.icon className={`w-5 h-5 ${displayType === dt.value ? 'text-blue-600' : 'text-gray-400'}`} />
                                                <span className={`text-xs font-medium ${displayType === dt.value ? 'text-blue-600' : 'text-gray-500'}`}>
                                                    {dt.label}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Selected items chips - Services or Categories */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {sectionType === 'services'
                                            ? `Services in Section (${sectionServices.length})`
                                            : `Categories in Section (${sectionCategories.length})`
                                        }
                                    </label>
                                    <div className="border border-gray-200 rounded-lg p-3 min-h-[120px] max-h-[200px] overflow-y-auto">
                                        {sectionType === 'services' ? (
                                            sectionServices.length === 0 ? (
                                                <p className="text-sm text-gray-400 text-center py-4">
                                                    No services added yet. Select from the right panel.
                                                </p>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {sectionServices.map((s, i) => {
                                                        const service = s.serviceId
                                                        if (!service) return null
                                                        const serviceName = service && typeof service === 'object' ? service.name : 'Unknown'
                                                        const serviceId = service && typeof service === 'object' ? service._id : service
                                                        return (
                                                            <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm">
                                                                <span>{serviceName}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveService(serviceId)}
                                                                    className="p-0.5 hover:bg-blue-100 rounded-full"
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )
                                        ) : (
                                            sectionCategories.length === 0 ? (
                                                <p className="text-sm text-gray-400 text-center py-4">
                                                    No categories added yet. Select from the right panel.
                                                </p>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {sectionCategories.map((c, i) => {
                                                        const item = c.itemType === 'category' ? c.categoryId : c.subCategoryId
                                                        if (!item) return null
                                                        const itemName = item && typeof item === 'object' ? item.name : 'Unknown'
                                                        const itemId = item && typeof item === 'object' ? item._id : item
                                                        const isCategory = c.itemType === 'category'
                                                        return (
                                                            <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                                                                isCategory
                                                                    ? 'bg-purple-50 text-purple-700'
                                                                    : 'bg-indigo-50 text-indigo-700'
                                                            }`}>
                                                                <span className={`text-xs mr-1 ${
                                                                    isCategory ? 'text-purple-400' : 'text-indigo-400'
                                                                }`}>
                                                                    {isCategory ? 'CAT' : 'SUB'}
                                                                </span>
                                                                <span>{itemName}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveCategory(itemId, c.itemType)}
                                                                    className={`p-0.5 rounded-full ${
                                                                        isCategory ? 'hover:bg-purple-100' : 'hover:bg-indigo-100'
                                                                    }`}
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* ── Right Column: Type toggle + Cascading dropdowns ── */}
                            <div className="space-y-4">
                                <h3 className="font-medium text-gray-900 border-b pb-2">Add Items</h3>

                                {/* Section Type Toggle */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Section Type</label>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleSectionTypeChange('services')}
                                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                                sectionType === 'services'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            <Layers className="w-4 h-4" />
                                            Services
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleSectionTypeChange('categories')}
                                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                                sectionType === 'categories'
                                                    ? 'bg-purple-600 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            <Grid3X3 className="w-4 h-4" />
                                            Categories
                                        </button>
                                    </div>
                                </div>

                                {/* Group dropdown - always visible */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Group</label>
                                    <select
                                        value={selectedGroup?._id || ''}
                                        onChange={(e) => handleGroupChange(e.target.value)}
                                        className={selectClass}
                                    >
                                        <option value="">Select a group...</option>
                                        {parentServices.map(g => (
                                            <option key={g._id} value={g._id}>{g.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* ── Services Flow ── */}
                                {sectionType === 'services' && (
                                    <>
                                        {/* Category dropdown */}
                                        {selectedGroup && (
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1.5">Category</label>
                                                {loadingCategories ? (
                                                    <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg">
                                                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                                                        <span className="text-sm text-gray-400">Loading categories...</span>
                                                    </div>
                                                ) : (
                                                    <select
                                                        value={selectedCategory?._id || ''}
                                                        onChange={(e) => handleCategoryChange(e.target.value)}
                                                        className={selectClass}
                                                    >
                                                        <option value="">Select a category...</option>
                                                        {categories.map(c => (
                                                            <option key={c._id} value={c._id}>{c.name}</option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        )}

                                        {/* SubCategory dropdown */}
                                        {selectedCategory && (
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1.5">Sub Category</label>
                                                {loadingSubCategories ? (
                                                    <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg">
                                                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                                                        <span className="text-sm text-gray-400">Loading subcategories...</span>
                                                    </div>
                                                ) : (
                                                    <select
                                                        value={selectedSubCategory?._id || ''}
                                                        onChange={(e) => handleSubCategoryChange(e.target.value)}
                                                        className={selectClass}
                                                    >
                                                        <option value="">Select a subcategory...</option>
                                                        {subCategories.map(s => (
                                                            <option key={s._id} value={s._id}>{s.name}</option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        )}

                                        {/* Services list */}
                                        {selectedSubCategory && (
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                                                    Services {services.length > 0 && `(${services.length})`}
                                                </label>
                                                <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                                                    {loadingServices ? (
                                                        <div className="flex items-center justify-center py-8">
                                                            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                                        </div>
                                                    ) : services.length === 0 ? (
                                                        <p className="text-sm text-gray-400 text-center py-6">No services found</p>
                                                    ) : services.filter(service => {
                                                        if (gender === 'all') return true
                                                        if (!service.gender || service.gender.toLowerCase() === 'all') return true
                                                        return service.gender.toLowerCase() === gender.toLowerCase()
                                                    }).map((service) => {
                                                        const isAdded = isServiceInSection(service._id)
                                                        const isAdding = addingService === service._id
                                                        return (
                                                            <div key={service._id} className="flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                                                                {service.img ? (
                                                                    <img src={service.img} alt={service.name} className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                                                                ) : (
                                                                    <div className="w-9 h-9 rounded-lg bg-gray-100 flex-shrink-0" />
                                                                )}
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium text-gray-900 truncate">{service.name}</p>
                                                                    <p className="text-xs text-gray-500">
                                                                        ₹{service.price}
                                                                        {service.gender && (
                                                                            <span className={`ml-1.5 ${service.gender === 'Male' ? 'text-blue-500' : 'text-pink-500'}`}>
                                                                                • {service.gender}
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                </div>
                                                                {isAdded ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveService(service._id)}
                                                                        className="p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 flex-shrink-0"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleAddService(service)}
                                                                        disabled={isAdding}
                                                                        className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 disabled:opacity-50 flex-shrink-0"
                                                                    >
                                                                        {isAdding
                                                                            ? <Loader2 className="w-4 h-4 animate-spin" />
                                                                            : <Plus className="w-4 h-4" />
                                                                        }
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* ── Categories Flow ── */}
                                {sectionType === 'categories' && selectedGroup && (
                                    <>
                                        {/* Category vs Subcategory toggle */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1.5">Add as</label>
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleCategoryItemTypeChange('category')}
                                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                        categoryItemType === 'category'
                                                            ? 'bg-purple-600 text-white'
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    <Grid3X3 className="w-4 h-4" />
                                                    Categories
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleCategoryItemTypeChange('subcategory')}
                                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                        categoryItemType === 'subcategory'
                                                            ? 'bg-indigo-600 text-white'
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    <Layers className="w-4 h-4" />
                                                    Subcategories
                                                </button>
                                            </div>
                                        </div>

                                        {/* ── Add Categories ── */}
                                        {categoryItemType === 'category' && (
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                                                    Categories {categories.length > 0 && `(${categories.length})`}
                                                </label>
                                                <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                                                    {loadingCategories ? (
                                                        <div className="flex items-center justify-center py-8">
                                                            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                                        </div>
                                                    ) : categories.length === 0 ? (
                                                        <p className="text-sm text-gray-400 text-center py-6">No categories found</p>
                                                    ) : categories.map((category) => {
                                                        const isAdded = isCategoryInSection(category._id, 'category')
                                                        const isAdding = addingCategory === category._id
                                                        return (
                                                            <div key={category._id} className="flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                                                                {category.img ? (
                                                                    <img src={category.img} alt={category.name} className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                                                                ) : (
                                                                    <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                                                                        <Grid3X3 className="w-4 h-4 text-purple-500" />
                                                                    </div>
                                                                )}
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium text-gray-900 truncate">{category.name}</p>
                                                                    <p className="text-xs text-purple-500">Category</p>
                                                                </div>
                                                                {isAdded ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveCategory(category._id, 'category')}
                                                                        className="p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 flex-shrink-0"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleAddCategory(category, 'category')}
                                                                        disabled={isAdding}
                                                                        className="p-1.5 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 disabled:opacity-50 flex-shrink-0"
                                                                    >
                                                                        {isAdding
                                                                            ? <Loader2 className="w-4 h-4 animate-spin" />
                                                                            : <Plus className="w-4 h-4" />
                                                                        }
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* ── Add Subcategories ── */}
                                        {categoryItemType === 'subcategory' && (
                                            <>
                                                {/* Category dropdown to browse subcategories */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Select Category</label>
                                                    {loadingCategories ? (
                                                        <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg">
                                                            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                                                            <span className="text-sm text-gray-400">Loading categories...</span>
                                                        </div>
                                                    ) : (
                                                        <select
                                                            value={selectedCategory?._id || ''}
                                                            onChange={(e) => handleCategoryChange(e.target.value)}
                                                            className={selectClass}
                                                        >
                                                            <option value="">Select a category...</option>
                                                            {categories.map(c => (
                                                                <option key={c._id} value={c._id}>{c.name}</option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </div>

                                                {/* Subcategories list */}
                                                {selectedCategory && (
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1.5">
                                                            Subcategories {subCategories.length > 0 && `(${subCategories.length})`}
                                                        </label>
                                                        <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[280px] overflow-y-auto">
                                                            {loadingSubCategories ? (
                                                                <div className="flex items-center justify-center py-8">
                                                                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                                                </div>
                                                            ) : subCategories.length === 0 ? (
                                                                <p className="text-sm text-gray-400 text-center py-6">No subcategories found</p>
                                                            ) : subCategories.map((subCategory) => {
                                                                const isAdded = isCategoryInSection(subCategory._id, 'subcategory')
                                                                const isAdding = addingCategory === subCategory._id
                                                                return (
                                                                    <div key={subCategory._id} className="flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                                                                        {subCategory.img ? (
                                                                            <img src={subCategory.img} alt={subCategory.name} className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                                                                        ) : (
                                                                            <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                                                                <Layers className="w-4 h-4 text-indigo-500" />
                                                                            </div>
                                                                        )}
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-sm font-medium text-gray-900 truncate">{subCategory.name}</p>
                                                                            <p className="text-xs text-indigo-500">Subcategory</p>
                                                                        </div>
                                                                        {isAdded ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleRemoveCategory(subCategory._id, 'subcategory')}
                                                                                className="p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 flex-shrink-0"
                                                                            >
                                                                                <Trash2 className="w-4 h-4" />
                                                                            </button>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleAddCategory(subCategory, 'subcategory')}
                                                                                disabled={isAdding}
                                                                                className="p-1.5 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200 disabled:opacity-50 flex-shrink-0"
                                                                            >
                                                                                {isAdding
                                                                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                                                                    : <Plus className="w-4 h-4" />
                                                                                }
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 pt-6 mt-6 border-t">
                            <button
                                type="button"
                                onClick={() => onClose(false)}
                                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                            >
                                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                                {isEditing ? 'Save Changes' : 'Create Section'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default SectionModal
